import React, { useState, useEffect, useMemo, useRef } from 'react';

const FinancialAnalysisResultsSection = ({ extractedData, isDataLoaded, onAnalysisCalculated }) => {
  const [activeTab, setActiveTab] = useState('extract');
  const [analysisData, setAnalysisData] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const lastCalculatedRef = useRef(null); // Track last calculated result to prevent unnecessary updates

  // Helper to get value from pivot data
  const getValue = (data, metricName, year) => {
    const row = data.find(r => r.name === metricName);
    return row && row[year] !== undefined ? parseFloat(row[year]) : 0;
  };

  // Calculate Financial Analysis from extracted data
  const calculateFinancialAnalysis = (pivotData, years) => {
    const sortedYears = [...years].sort();
    
    // 1. Financial Highlight
    const highlightMetrics = [
      'pendapatan / penjualan / revenue / sales',
      'HPP / COGS / harga pokok penjualan',
      'laba kotor / gross profit',
      'laba usaha / operating income',
      'laba sebelum pajak / profit before tax',
      'laba bersih / net profit'
    ];

    const highlightMapping = {
      'pendapatan / penjualan / revenue / sales': 'Sales',
      'HPP / COGS / harga pokok penjualan': 'COGS',
      'laba kotor / gross profit': 'Gross Profit',
      'laba usaha / operating income': 'Operating Income',
      'laba sebelum pajak / profit before tax': 'Profit/(Loss) Before Tax',
      'laba bersih / net profit': 'Net Profit / (Loss)'
    };

    const financialHighlight = [];
    highlightMetrics.forEach(metric => {
      const row = { 'FINANCIAL HIGHLIGHT': highlightMapping[metric] };
      
      sortedYears.forEach(year => {
        const value = getValue(pivotData, metric, year);
        const sales = getValue(pivotData, 'pendapatan / penjualan / revenue / sales', year);
        
        row[`Dec-${year}`] = value.toFixed(2);
        
        if (metric === 'pendapatan / penjualan / revenue / sales') {
          row[`% ${year}`] = '100.00%';
        } else if (sales !== 0) {
          row[`% ${year}`] = `${((value / sales) * 100).toFixed(2)}%`;
        } else {
          row[`% ${year}`] = '0.00%';
        }
      });
      
      financialHighlight.push(row);
    });

    // 2. Financial Ratios
    const financialRatios = [];
    
    // Add separator
    financialHighlight.push({ 'FINANCIAL HIGHLIGHT': '' });
    financialHighlight.push({ 'FINANCIAL HIGHLIGHT': 'FINANCIAL RATIO' });

    const ratios = [
      'Revenue Growth',
      'Current Ratio (X)',
      'Interest Cover',
      'EBITDA',
      'Debt Service Ratio (DSR)',
      'Leverage ratio (x)',
      'Interest Expense',
      'CPLTD'
    ];

    ratios.forEach(ratioName => {
      const row = { 'FINANCIAL HIGHLIGHT': ratioName };
      
      sortedYears.forEach((year, idx) => {
        const sales = getValue(pivotData, 'pendapatan / penjualan / revenue / sales', year);
        const prevSales = idx > 0 ? getValue(pivotData, 'pendapatan / penjualan / revenue / sales', sortedYears[idx - 1]) : 0;
        const currentAssets = getValue(pivotData, 'jumlah asset lancar', year);
        const currentLiabilities = getValue(pivotData, 'jumlah kewajiban jangka pendek', year);
        const interestExpense = getValue(pivotData, 'bunga pinjaman (rupiah)', year);
        const depreciation = getValue(pivotData, 'jumlah beban penyusutan', year);
        const taxIncome = getValue(pivotData, 'beban pajak penghasilan', year);
        const netProfit = getValue(pivotData, 'laba bersih / net profit', year);
        const longTermDebt = getValue(pivotData, 'jumlah kewajiban jangka panjang', year);
        const equity = getValue(pivotData, 'jumlah equitas', year);
        const cpltdValue = getValue(pivotData, 'utang jangka panjang yang jatuh tempo dalam setahun', year);

        let value;
        switch (ratioName) {
          case 'Revenue Growth':
            value = (idx === 0 || prevSales === 0) ? '0.00' : `${(((sales - prevSales) / prevSales) * 100).toFixed(2)}%`;
            break;
          case 'Current Ratio (X)':
            value = currentLiabilities !== 0 ? `${((currentAssets / currentLiabilities) * 100).toFixed(2)}%` : '0.00%';
            break;
          case 'EBITDA':
            value = (depreciation + netProfit + interestExpense + taxIncome).toFixed(2);
            break;
          case 'Interest Cover':
            const ebitda = depreciation + netProfit + interestExpense + taxIncome;
            value = interestExpense !== 0 ? (ebitda / interestExpense).toFixed(2) : '0.00';
            break;
          case 'Debt Service Ratio (DSR)':
            const ebitda2 = depreciation + netProfit + interestExpense + taxIncome;
            const dsrDenom = interestExpense + cpltdValue;
            value = dsrDenom !== 0 ? (ebitda2 / dsrDenom).toFixed(2) : '0.00';
            break;
          case 'Leverage ratio (x)':
            const totalLiabilities = longTermDebt + currentLiabilities;
            value = equity !== 0 ? (totalLiabilities / equity).toFixed(2) : '0.00';
            break;
          case 'Interest Expense':
            value = interestExpense.toFixed(2);
            break;
          case 'CPLTD':
            value = cpltdValue.toFixed(2);
            break;
          default:
            value = '0.00';
        }

        row[`Dec-${year}`] = value;
        row[`% ${year}`] = '';
      });
      
      financialHighlight.push(row);
    });

    // 3. WI Cycle
    financialHighlight.push({ 'FINANCIAL HIGHLIGHT': '' });
    financialHighlight.push({ 'FINANCIAL HIGHLIGHT': 'WI CYCLE' });

    const wiCycleMetrics = [
      'AR', 'INV', 'AP', 'AE',
      'AR DOH', 'INV DOH', 'AP DOH', 'AE DOH', 'WI DOH'
    ];

    wiCycleMetrics.forEach(metricName => {
      const row = { 'FINANCIAL HIGHLIGHT': metricName };
      
      sortedYears.forEach(year => {
        const sales = getValue(pivotData, 'pendapatan / penjualan / revenue / sales', year);
        const cogs = getValue(pivotData, 'HPP / COGS / harga pokok penjualan', year);
        const ar = getValue(pivotData, 'piutang usaha', year);
        const inv = getValue(pivotData, 'persediaan', year);
        const ap = getValue(pivotData, 'hutang usaha', year);
        const ae = getValue(pivotData, 'biaya yang masih harus dibayar', year);

        let value;
        switch (metricName) {
          case 'AR':
            value = ar.toFixed(2);
            break;
          case 'INV':
            value = inv.toFixed(2);
            break;
          case 'AP':
            value = ap.toFixed(2);
            break;
          case 'AE':
            value = ae.toFixed(2);
            break;
          case 'AR DOH':
            value = sales !== 0 ? ((ar / sales) * 365).toFixed(2) : '0.00';
            break;
          case 'INV DOH':
            value = sales !== 0 ? ((inv / sales) * 365).toFixed(2) : '0.00';
            break;
          case 'AP DOH':
            value = cogs !== 0 ? ((ap / cogs) * 365).toFixed(2) : '0.00';
            break;
          case 'AE DOH':
            value = cogs !== 0 ? ((ae / cogs) * 365).toFixed(2) : '0.00';
            break;
          case 'WI DOH':
            const arDoh = sales !== 0 ? (ar / sales) * 365 : 0;
            const invDoh = sales !== 0 ? (inv / sales) * 365 : 0;
            const apDoh = cogs !== 0 ? (ap / cogs) * 365 : 0;
            const aeDoh = cogs !== 0 ? (ae / cogs) * 365 : 0;
            value = ((arDoh + invDoh) - (apDoh + aeDoh)).toFixed(2);
            break;
          default:
            value = '0.00';
        }

        row[`Dec-${year}`] = value;
        row[`% ${year}`] = '';
      });
      
      financialHighlight.push(row);
    });

    // 4. YoY Analysis
    const yoyAnalysis = [];
    pivotData.forEach(row => {
      const yoyRow = { 'ACCOUNT': row.name };
      
      sortedYears.forEach((year, idx) => {
        const value = row[year] !== undefined ? parseFloat(row[year]) : 0;
        yoyRow[`Dec-${year}`] = value.toFixed(2);
        
        if (idx === 0) {
          yoyRow[`YoY % ${year}`] = '-';
        } else {
          const prevYear = sortedYears[idx - 1];
          const prevValue = row[prevYear] !== undefined ? parseFloat(row[prevYear]) : 0;
          
          if (prevValue !== 0) {
            const yoyChange = ((value - prevValue) / prevValue) * 100;
            yoyRow[`YoY % ${year}`] = `${yoyChange.toFixed(2)}%`;
          } else {
            yoyRow[`YoY % ${year}`] = '-';
          }
        }
      });
      
      yoyAnalysis.push(yoyRow);
    });

    // Build columns
    const columns = ['FINANCIAL HIGHLIGHT'];
    sortedYears.forEach(year => {
      columns.push(`Dec-${year}`);
      columns.push(`% ${year}`);
    });

    const yoyColumns = ['ACCOUNT'];
    sortedYears.forEach(year => {
      yoyColumns.push(`Dec-${year}`);
      yoyColumns.push(`YoY % ${year}`);
    });

    return {
      complete_analysis: financialHighlight,
      financial_summary: financialHighlight.filter(row => 
        !row['FINANCIAL HIGHLIGHT'].includes('RATIO') && 
        !row['FINANCIAL HIGHLIGHT'].includes('WI CYCLE') &&
        row['FINANCIAL HIGHLIGHT'] !== ''
      ).slice(0, 6),
      yoy_analysis: yoyAnalysis,
      columns: columns,
      yoy_columns: yoyColumns
    };
  };

  // Initialize editable data when extractedData changes
  useEffect(() => {
    if (extractedData && extractedData.pivotData) {
      setEditableData(JSON.parse(JSON.stringify(extractedData.pivotData)));
      setHasChanges(false); // Reset changes flag on new data
      lastCalculatedRef.current = null; // Reset last calculated reference
    }
  }, [extractedData]);

  // Calculate analysis from editable data using useMemo to prevent recalculation
  const calculatedAnalysis = useMemo(() => {
    if (editableData && extractedData?.years) {
      return calculateFinancialAnalysis(editableData, extractedData.years);
    }
    return null;
  }, [editableData, extractedData?.years]);

  // Update analysis data and notify parent only when calculated result actually changes
  useEffect(() => {
    if (calculatedAnalysis) {
      // Only update if the result has actually changed (string comparison for deep equality check)
      const currentResultStr = JSON.stringify(calculatedAnalysis);
      const lastResultStr = lastCalculatedRef.current ? JSON.stringify(lastCalculatedRef.current) : null;
      
      if (currentResultStr !== lastResultStr) {
        setAnalysisData(calculatedAnalysis);
        lastCalculatedRef.current = calculatedAnalysis;
        
        // Pass calculated data to parent only when it changes
        if (onAnalysisCalculated) {
          onAnalysisCalculated(calculatedAnalysis);
        }
      }
    }
  }, [calculatedAnalysis, onAnalysisCalculated]);

  // Format currency with thousand separators
  const formatCurrency = (value, metricName = '') => {
    if (!value || value === '-' || value === '') return value;
    
    // Check if it's a percentage (contains %)
    if (typeof value === 'string' && value.includes('%')) {
      return value;
    }
    
    // Convert to number and format
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    // Metrics that should display with 2 decimals (ratios, multipliers, DOH values)
    const decimalMetrics = [
      'Interest Cover',
      'Debt Service Ratio (DSR)',
      'Leverage ratio (x)',
      'AR DOH',
      'INV DOH',
      'AP DOH',
      'AE DOH',
      'WI DOH'
    ];
    
    // Check if it's a ratio/multiplier (usually small numbers < 1000)
    const isRatioValue = numValue < 1000 && numValue !== 0;
    const isDecimalMetric = decimalMetrics.includes(metricName);
    
    // Format with 2 decimals for ratios, without decimals for large amounts
    if (isDecimalMetric || isRatioValue) {
      return numValue.toLocaleString('id-ID', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    
    // Format large amounts without decimals
    return numValue.toLocaleString('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  if (!isDataLoaded || !extractedData) {
    return (
      <section className="mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
          <div className="px-6 py-12 text-center">
            <i className="fa-solid fa-chart-pie text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <p className="text-gray-500 dark:text-gray-400 transition-colors duration-300">Upload laporan keuangan untuk melihat analisis lengkap</p>
          </div>
        </div>
      </section>
    );
  }

  const handleCellEdit = (rowIdx, year, newValue) => {
    // Parse the input value (remove thousand separators)
    const cleanValue = newValue.replace(/\./g, '').replace(/,/g, '.');
    const numValue = parseFloat(cleanValue);
    
    if (isNaN(numValue)) {
      return; // Invalid number
    }

    // Update editable data
    const updatedData = [...editableData];
    const oldValue = updatedData[rowIdx][year];
    
    if (oldValue !== numValue) {
      updatedData[rowIdx][year] = numValue;
      setEditableData(updatedData);
      setHasChanges(true);
    }
    
    setEditingCell(null);
  };

  const handleResetData = () => {
    if (window.confirm('Reset semua perubahan ke data asli dari ekstraksi LLM?')) {
      setEditableData(JSON.parse(JSON.stringify(extractedData.pivotData)));
      setHasChanges(false);
    }
  };

  const handleCellClick = (rowIdx, yearIdx) => {
    setEditingCell({ rowIdx, yearIdx });
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const handleKeyPress = (e, rowIdx, year) => {
    if (e.key === 'Enter') {
      handleCellEdit(rowIdx, year, e.target.value);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const renderExtractedTable = () => {
    if (!editableData || !extractedData?.years) return null;

    const columns = ['Metric Name', ...extractedData.years.map(y => `Year ${y}`)];

    return (
      <div className="overflow-x-auto">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex-1 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg transition-colors duration-300">
            <div className="flex items-center text-sm text-yellow-800 dark:text-yellow-200 transition-colors duration-300">
              <i className="fa-solid fa-pen-to-square mr-2"></i>
              <span><strong>Editable Table:</strong> Klik pada nilai untuk mengedit. Tekan Enter untuk menyimpan atau Escape untuk membatalkan.</span>
            </div>
          </div>
          {hasChanges && (
            <div className="ml-3 flex items-center space-x-2">
              <span className="px-3 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs rounded-lg flex items-center transition-colors duration-300">
                <i className="fa-solid fa-exclamation-circle mr-2"></i>
                Data diubah
              </span>
              <button
                onClick={handleResetData}
                className="px-3 py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 text-xs rounded-lg flex items-center transition-colors"
              >
                <i className="fa-solid fa-rotate-left mr-2"></i>
                Reset
              </button>
            </div>
          )}
        </div>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-indigo-50 dark:bg-indigo-900/30 transition-colors duration-300">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className="px-6 py-3 text-left text-xs font-medium text-indigo-700 dark:text-indigo-300 uppercase tracking-wider transition-colors duration-300"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {editableData.map((row, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors">
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700/50 transition-colors duration-300">
                  {row.name}
                </td>
                {extractedData.years.map((year, yearIdx) => {
                  const isEditing = editingCell?.rowIdx === rowIdx && editingCell?.yearIdx === yearIdx;
                  const cellValue = row[year] || 0;
                  
                  return (
                    <td 
                      key={yearIdx} 
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors relative group"
                      onClick={() => !isEditing && handleCellClick(rowIdx, yearIdx)}
                    >
                      {isEditing ? (
                        <input
                          type="text"
                          defaultValue={cellValue}
                          autoFocus
                          className="w-full px-2 py-1 bg-white dark:bg-gray-700 border border-indigo-500 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-300"
                          onBlur={(e) => handleCellEdit(rowIdx, year, e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, rowIdx, year)}
                        />
                      ) : (
                        <>
                          {formatCurrency(cellValue, row.name)}
                          <i className="fa-solid fa-pen text-xs text-indigo-400 dark:text-indigo-500 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors duration-300">
          <div className="flex items-center text-xs text-blue-700 dark:text-blue-300 transition-colors duration-300">
            <i className="fa-solid fa-info-circle mr-2"></i>
            <span>Setiap perubahan data akan otomatis mengupdate kalkulasi di tab "Analisis Lengkap"</span>
          </div>
        </div>
      </div>
    );
  };

  const renderAnalysisTable = (data, columns) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700 transition-colors duration-300">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap transition-colors duration-300"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((row, rowIdx) => {
              const isHeader = row[columns[0]] === 'FINANCIAL RATIO' || 
                              row[columns[0]] === 'WI CYCLE' || 
                              row[columns[0]] === '';
              const metricName = row[columns[0]];
              
              return (
                <tr 
                  key={rowIdx}
                  className={isHeader ? 'bg-gray-100 dark:bg-gray-700 font-semibold' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
                >
                  {columns.map((col, colIdx) => (
                    <td
                      key={colIdx}
                      className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                        colIdx === 0 ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {colIdx === 0 ? (row[col] || '-') : formatCurrency(row[col], metricName)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className="mb-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium text-gray-800 dark:text-white flex items-center transition-colors duration-300">
              <i className="fa-solid fa-table mr-2 text-indigo-600 dark:text-indigo-400"></i>
              Hasil Analisis Keuangan
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">
              Extract LLM, Financial Highlights, Ratios & Working Capital Cycle
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('extract')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'extract'
                  ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <i className="fa-solid fa-brain mr-2"></i>
              Extract LLM
            </button>
            <button
              onClick={() => setActiveTab('complete')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'complete'
                  ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <i className="fa-solid fa-chart-bar mr-2"></i>
              Analisis Lengkap
            </button>
            <button
              onClick={() => setActiveTab('summary')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'summary'
                  ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <i className="fa-solid fa-file-lines mr-2"></i>
              Financial Highlight
            </button>
            <button
              onClick={() => setActiveTab('yoy')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'yoy'
                  ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <i className="fa-solid fa-arrow-trend-up mr-2"></i>
              YoY Analysis
            </button>
          </nav>
        </div>

        {/* Table Content */}
        <div className="p-6">
          {activeTab === 'extract' && renderExtractedTable()}
          {activeTab === 'complete' && analysisData && renderAnalysisTable(
            analysisData.complete_analysis,
            analysisData.columns
          )}
          {activeTab === 'summary' && analysisData && renderAnalysisTable(
            analysisData.financial_summary,
            analysisData.columns
          )}
          {activeTab === 'yoy' && analysisData && renderAnalysisTable(
            analysisData.yoy_analysis,
            analysisData.yoy_columns
          )}
        </div>

        {/* Footer Stats */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 transition-colors duration-300">
                {extractedData?.pivotData?.length || 0}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">Metrik Diekstrak</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 transition-colors duration-300">
                {extractedData?.years?.length || 0}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">Tahun</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 transition-colors duration-300">
                {analysisData?.complete_analysis?.length || 0}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">Total Analisis</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinancialAnalysisResultsSection;
